import { InformationCircleIcon } from "@heroicons/react/24/outline";
import React from "react";
import { Link } from "react-router-dom";

import { Collapsible, Label } from "@fronterahealth/frontera-ui-components";

import { ApiReportReportTypeChoices, ApiReportTemplateFileUploadConfigChoices } from "@api/graphql/types-and-hooks";
import { useAdminData } from "@providers/AdminDataProvider";

interface Category {
  title: string;
  description: React.ReactNode;
}

const ScoringTemplatesLink: React.FC = () => (
  <>
    Find our Frontera assessment scoring templates{" "}
    <Link to="/downloads" className="text-interactive-primary-default underline">
      here
    </Link>
    .
  </>
);

const DEFAULT_CATEGORIES: Category[] = [
  {
    title: "Assessment",
    description: (
      <>
        Formal evaluation results from standardized instruments (e.g., ADOS-2, WISC-V, CARS-2). Includes scoring sheets,
        protocols, and assessment reports. <ScoringTemplatesLink />
      </>
    ),
  },
  {
    title: "Behavioral Observation",
    description:
      "Notes, videos, or reports documenting observed behaviors in various settings such as home, school, or clinical environments.",
  },
  {
    title: "Interview",
    description:
      "Audio or video recordings of interviews with parents, guardians, teachers, or the client. May also include interview transcripts or notes.",
  },
  {
    title: "Supporting Document",
    description:
      "Additional materials such as medical records, previous evaluations, school reports, IEPs, therapy notes, or other relevant documentation.",
  },
];

const DB_ABC_CATEGORIES: Category[] = [
  {
    title: "Assessment",
    description: (
      <>
        Formal evaluation results from standardized instruments (e.g., ADOS-2, WISC-V, CARS-2). Includes scoring sheets,
        protocols, and assessment reports. <ScoringTemplatesLink />
      </>
    ),
  },
  {
    title: "Interview",
    description:
      "Audio or video recordings of interviews with parents, guardians, teachers, or the client. May also include interview transcripts or notes.",
  },
  {
    title: "Supporting Document",
    description:
      "Additional materials such as medical records, previous evaluations, school reports, IEPs, therapy notes, or other relevant documentation.",
  },
];

const DB_ABC_KUWAIT_CATEGORIES: Category[] = [
  {
    title: "Interview",
    description:
      "Audio or video recordings of interviews with parents, guardians, teachers, or the client. May also include interview transcripts or notes.",
  },
];

const IA_BASE_CATEGORIES: Category[] = [
  {
    title: "Interview",
    description:
      "Audio or video recordings of interviews with parents, caregivers, teachers, or the client. May also include interview transcripts or notes.",
  },
  {
    title: "Diagnosis Report",
    description: "Diagnosis report from the psychologist that contains the autism diagnosis.",
  },
  {
    title: "Behavioral Observations",
    description:
      "Notes, videos, or reports documenting observed behaviors in various settings such as home, school, or clinical environments.",
  },
  {
    title: "Assessments",
    description: (
      <>
        Formal evaluation results from standardized instruments (e.g., ABLLS-R, AFLS, PEAK). Includes scoring sheets,
        protocols, and assessment reports. <ScoringTemplatesLink />
      </>
    ),
  },
  {
    title: "Clinical Notes",
    description:
      "Anything not part of the intake — prior reports, IEPs, 504 plans, medical records, OT notes, or other supporting clinical documentation.",
  },
];

const IA_ACES_CATEGORIES: Category[] = [
  {
    title: "Interview",
    description:
      'Recording = Audio or video recordings of interviews with parents, caregivers, teachers, or the client. May also include interview transcripts. Interview & Observation Notes = Structured notes from parent interviews and direct observations. Mark any "behavioral observations" sections explicitly so they can be summarized correctly.',
  },
  {
    title: "BHT / Intake",
    description: "BHT form (BHTIA) for clients on the CCS template, or the ACES intake form for non-CCS clients.",
  },
  {
    title: "Diagnosis Report",
    description: "Diagnosis report from the psychologist that contains the autism diagnosis.",
  },
  {
    title: "Assessment",
    description: (
      <>
        Formal evaluation results from standardized instruments (e.g., ABLLS-R, AFLS, PCA PEAK). Includes scoring
        sheets, protocols, and assessment reports. <ScoringTemplatesLink />
      </>
    ),
  },
];

const IA_CATALIGHT_CATEGORIES: Category[] = [
  {
    title: "Interview",
    description:
      'Recording = Audio or video recordings of interviews with parents, caregivers, teachers, or the client. May also include interview transcripts.\nInterview & Observation Notes = Structured notes from parent interviews and direct observations. Mark any "behavioral observations" sections explicitly so they can be summarized correctly.',
  },
  { title: "BHTIA", description: "BHT Initial Assessment intake form." },
  {
    title: "Diagnosis Report",
    description: "Diagnosis report from the psychologist that contains the autism diagnosis.",
  },
  {
    title: "Assessment",
    description: (
      <>
        Formal evaluation results from standardized instruments (e.g., ABLLS-R, AFLS, PCA PEAK). Includes scoring
        sheets, protocols, and assessment reports. <ScoringTemplatesLink />
      </>
    ),
  },
];

const REASSESSMENT_CATEGORIES: Category[] = [
  {
    title: "Assessment",
    description: (
      <>
        Formal evaluation results from standardized instruments (e.g., ADOS-2, BASC-3, Vineland-3). Includes scoring
        sheets, protocols, and assessment reports. <ScoringTemplatesLink />
      </>
    ),
  },
  {
    title: "Prior Report",
    description: "The most recent ABA treatment plan submitted to insurance for this client.",
  },
];

const getCategories = (
  reportType: ApiReportReportTypeChoices | undefined,
  fileUploadConfig: ApiReportTemplateFileUploadConfigChoices | undefined,
  isAces: boolean,
  isAbc: boolean,
  isAbcKuwait: boolean,
): Category[] => {
  if (reportType === ApiReportReportTypeChoices.DiagnosisEvaluation) {
    if (isAbc) {
      return DB_ABC_CATEGORIES;
    }
    if (isAbcKuwait) {
      return DB_ABC_KUWAIT_CATEGORIES;
    }
    return DEFAULT_CATEGORIES;
  }
  if (reportType === ApiReportReportTypeChoices.InitialAssessment) {
    if (isAces) {
      return IA_ACES_CATEGORIES;
    }
    if (fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight) {
      return IA_CATALIGHT_CATEGORIES;
    }
    return IA_BASE_CATEGORIES;
  }
  if (reportType === ApiReportReportTypeChoices.Reassessment) {
    return REASSESSMENT_CATEGORIES;
  }
  return DEFAULT_CATEGORIES;
};

interface UploadDocumentsGuideProps {
  reportType: ApiReportReportTypeChoices | undefined;
  fileUploadConfig: ApiReportTemplateFileUploadConfigChoices | undefined;
}

export const UploadDocumentsGuide: React.FC<UploadDocumentsGuideProps> = ({ reportType, fileUploadConfig }) => {
  const { isAcesUser, isTestAcesUser, isABCUser, isKuwaitUser } = useAdminData();
  const categories = getCategories(reportType, fileUploadConfig, isAcesUser || isTestAcesUser, isABCUser, isKuwaitUser);
  return (
    <div className="border border-border-strong rounded-lg">
      <Collapsible
        headerIsClickable={true}
        headerContent={
          <div className="flex items-center gap-2 px-3 py-1">
            <InformationCircleIcon className="h-5 w-5 text-icon-tertiary" />
            <Label size="extra-small">Document Category Guide</Label>
          </div>
        }
        bodyContent={
          <div className="p-4 mt-2 border-t border-border-strong text-xs">
            {categories.map((cat, i) => (
              <div key={cat.title} className={i > 0 ? "border-t border-border-medium pt-4 mt-4" : ""}>
                <p className="font-semibold text-text-primary mb-1">{cat.title}</p>
                <p className="text-text-secondary">{cat.description}</p>
              </div>
            ))}
          </div>
        }
      />
    </div>
  );
};
